import React, { useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';

/**
 * MagicBentoCard
 * Wraps children with spotlight, border glow, floating particles,
 * 3D tilt, magnetism, and ripple-click effects — adapted for light theme.
 *
 * Props:
 *   glowColor       RGB string, default "9, 132, 227" (nexcess-blue)
 *   particleCount   number, default 10
 *   enableTilt      boolean, default true
 *   enableMagnetism boolean, default true
 *   enableSpotlight boolean, default true
 *   enableBorderGlow boolean, default true
 *   enableStars     boolean, default true
 *   clickEffect     boolean, default true
 *   className       extra classes on the outer wrapper
 *   style           extra styles on the outer wrapper
 */
const MagicBentoCard = ({
  children,
  glowColor = '9, 132, 227',
  particleCount = 10,
  enableTilt = true,
  enableMagnetism = true,
  enableSpotlight = true,
  enableBorderGlow = true,
  enableStars = true,
  clickEffect = true,
  className = '',
  style = {},
}) => {
  const cardRef = useRef(null);
  const particlesRef = useRef([]);
  const timeoutsRef = useRef([]);
  const isHoveredRef = useRef(false);

  const clearParticles = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    particlesRef.current.forEach((p) => {
      gsap.to(p, {
        scale: 0, opacity: 0, duration: 0.25, ease: 'back.in(1.7)',
        onComplete: () => p.parentNode?.removeChild(p),
      });
    });
    particlesRef.current = [];
  }, []);

  const spawnParticles = useCallback(() => {
    if (!cardRef.current || !isHoveredRef.current) return;
    const { width, height } = cardRef.current.getBoundingClientRect();

    for (let i = 0; i < particleCount; i++) {
      const id = setTimeout(() => {
        if (!isHoveredRef.current || !cardRef.current) return;
        const el = document.createElement('div');
        el.style.cssText = `
          position:absolute;width:4px;height:4px;border-radius:50%;
          background:rgba(${glowColor},0.9);
          box-shadow:0 0 6px rgba(${glowColor},0.5);
          pointer-events:none;z-index:20;
          left:${Math.random() * width}px;top:${Math.random() * height}px;
        `;
        cardRef.current.appendChild(el);
        particlesRef.current.push(el);

        gsap.fromTo(el, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.3, ease: 'back.out(1.7)' });
        gsap.to(el, {
          x: (Math.random() - 0.5) * 80,
          y: (Math.random() - 0.5) * 80,
          rotation: Math.random() * 360,
          duration: 2 + Math.random() * 2,
          ease: 'none', repeat: -1, yoyo: true,
        });
      }, i * 90);
      timeoutsRef.current.push(id);
    }
  }, [particleCount, glowColor]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    // Inject per-card CSS variables for spotlight / border glow
    const style = document.createElement('style');
    style.textContent = `
      .magic-card-spotlight::before {
        content:'';position:absolute;inset:0;pointer-events:none;border-radius:inherit;
        background:radial-gradient(300px circle at var(--mx,50%) var(--my,50%),
          rgba(${glowColor},0.10), transparent 80%);
        opacity:0;transition:opacity .3s;z-index:1;
      }
      .magic-card-spotlight:hover::before { opacity:1; }
      .magic-card-border-glow::after {
        content:'';position:absolute;inset:0;pointer-events:none;border-radius:inherit;
        padding:1px;
        background:radial-gradient(280px circle at var(--mx,50%) var(--my,50%),
          rgba(${glowColor},0.55), transparent 50%);
        -webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);
        mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);
        -webkit-mask-composite:xor;mask-composite:exclude;
        opacity:0;transition:opacity .3s;z-index:2;
      }
      .magic-card-border-glow:hover::after { opacity:1; }
    `;
    // Only inject once globally
    if (!document.getElementById('magic-bento-styles')) {
      style.id = 'magic-bento-styles';
      document.head.appendChild(style);
    }

    const onEnter = () => {
      isHoveredRef.current = true;
      if (enableStars) spawnParticles();
    };

    const onLeave = () => {
      isHoveredRef.current = false;
      clearParticles();
      gsap.to(el, { rotateX: 0, rotateY: 0, x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1,0.4)', overwrite: 'auto' });
    };

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const cx = rect.width / 2;
      const cy = rect.height / 2;

      // Update CSS vars for spotlight / border glow
      el.style.setProperty('--mx', `${x}px`);
      el.style.setProperty('--my', `${y}px`);

      if (enableTilt) {
        gsap.to(el, {
          rotateX: ((y - cy) / cy) * -10,
          rotateY: ((x - cx) / cx) * 10,
          transformPerspective: 900,
          transformOrigin: 'center center',
          duration: 0.2, ease: 'power2.out', overwrite: 'auto',
        });
      }
      if (enableMagnetism) {
        gsap.to(el, {
          x: (x - cx) * 0.04,
          y: (y - cy) * 0.04,
          duration: 0.3, ease: 'power2.out', overwrite: 'auto',
        });
      }
    };

    const onClick = (e) => {
      if (!clickEffect) return;
      const rect = el.getBoundingClientRect();
      const ripple = document.createElement('div');
      ripple.style.cssText = `
        position:absolute;width:10px;height:10px;border-radius:50%;
        background:rgba(${glowColor},0.35);
        left:${e.clientX - rect.left}px;top:${e.clientY - rect.top}px;
        pointer-events:none;z-index:50;transform:translate(-50%,-50%);
      `;
      el.appendChild(ripple);
      gsap.fromTo(ripple, { scale: 0, opacity: 1 }, {
        scale: 40, opacity: 0, duration: 0.7, ease: 'power2.out',
        onComplete: () => ripple.remove(),
      });
    };

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('mousemove', onMove);
    el.addEventListener('click', onClick);

    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('click', onClick);
      clearParticles();
    };
  }, [spawnParticles, clearParticles, enableTilt, enableMagnetism, enableStars, clickEffect, glowColor]);

  return (
    <div
      ref={cardRef}
      className={`magic-card-spotlight magic-card-border-glow ${className}`}
      style={{ position: 'relative', overflow: 'hidden', willChange: 'transform', ...style }}
    >
      {children}
    </div>
  );
};

export default MagicBentoCard;
